import React, { useState, useEffect } from 'react';
import { View, Pressable } from 'react-native';
import { useMutation } from '@apollo/client';
import styled from 'styled-components/native';
import isEqual from 'react-fast-compare';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faEdit,
  faMagnifyingGlass
} from '@fortawesome/free-solid-svg-icons';

import PreBidsDetail from 'app/src/components/Sales/PreBidsDetail';
import { UPDATE_SALE_ASSET } from 'app/src/components/Sales/Process/queries';
import Modal from 'app/src/elements/Modal';
import { Text } from 'app/src/styles';
import colors from 'app/src/styles/colors';
import { dollars, numberWithDelimiter } from 'app/src/utils/stringHelpers';
import BidHistory from '../BidHistory';
import CurrentBidDetails from './CurrentBidDetails';
import BidSuggestions from './BidSuggestions';
import AssetButtons from './AssetButtons';
import Notices from '../Notices';
import AwardBidTo from './AwardBidTo';
import ConfirmSoldAlert from '../ConfirmSoldAlert';

import {
  Title,
  Subtitle,
  Label,
  Split,
  Button,
  GOING_BUTTONS,
  LeftContainer,
  RightContainer,
  BorderedContainer,
  SuggestionsContainer,
} from '../elements';

import {
  CREATE_BID,
  CREATE_BID_FROM_PRE_BID,
  BID_WON,
  BIDDING_PASS,
  REMOVE_LAST_BID,
  UNDO_REMOVE_LAST_BID,
} from '../queries';
import {
  AWARD_BID
} from '../Bids/queries';
import WinnerInfoAlert from '../WinnerInfoAlert';
import SoldInfoModal from '../SoldInfoModal';

const STEP_OPTIONS = [25, 50, 100, 150, 200, 250, 500, 1000, 1500, 2500, 5000, 10000];

const COLORS = {
  bid: 'lawngreen',
  sold: 'darkgreen',
  pass: colors.elRed,
};

const BidSettings = styled(View)`
  flex-direction: row;
  margin-bottom: 10px;
  gap: 10px;
`;

const Bids = ({ saleAsset, canInput, sale, biddingNotice, updateSale }) => {
  const [price, setPrice] = useState('');
  const [editNext, setEditNext] = useState(false);
  const [nextManual, setNextManual] = useState('');
  const [showPreBidDetail, setShowPreBidDetail] = useState(false);
  const [showConfirmSoldAlert, setShowConfirmSoldAlert] = useState(false);
  const [isIf, setIsIf] = useState(false);
  const [disableSoldButtons, setDisableSoldButtons] = useState(false);
  const [bidEnteredTimestamp, setBidEnteredTimestamp] = useState();

  const [createBid] = useMutation(CREATE_BID, {
    onCompleted: () => setPrice('')
  });

  const [createBidFromPreBid] = useMutation(CREATE_BID_FROM_PRE_BID);

  const [bidWon] = useMutation(BID_WON, {
    onCompleted: () => setPrice('')
  });

  const [awardBid] = useMutation(AWARD_BID, {
    onCompleted: () => setPrice('')
  });

  const [biddingPass] = useMutation(BIDDING_PASS, {
    onCompleted: () => setPrice('')
  });

  const [removeLastBid] = useMutation(REMOVE_LAST_BID, {
    variables: { saleAssetId: saleAsset?.id }
  });

  const [undoRemoveLastBid] = useMutation(UNDO_REMOVE_LAST_BID, {
    variables: { saleAssetId: saleAsset?.id }
  });

  const [updateSaleAsset] = useMutation(UPDATE_SALE_ASSET, {
    fetchPolicy: 'no-cache',
    onCompleted: () => {
      setEditNext(false);
      setNextManual('');
    }
  });

  const updateSaleAssetField = (field, value) => {
    updateSaleAsset({
      fetchPolicy: 'no-cache',
      variables: { id: saleAsset.id, [field]: value }
    });
  };

  const updateBiddingStatus = status => {
    updateSaleAssetField('biddingStatus', status);
  };

  const updateNextManual = () => {
    updateSaleAssetField(
      'biddingNextManual',
      nextManual ? parseInt(nextManual) : null
    );
  };

  const updateBiddingStep = value => {
    updateSaleAssetField('biddingStep', parseInt(value));
  }

  let highBid;
  let bids = [];

  console.log("High Bid", highBid)
  console.log("Bids", bids)
  console.log("disableSoldButtons", disableSoldButtons)

  bids = saleAsset ? [...saleAsset.bids].reverse() : [];
  highBid = parseFloat(saleAsset?.highBid || '0');

  const highBidDetails = bids?.find(bid =>
    bid.price === saleAsset?.highBid && !bid.removedAt
  )

  useEffect(() => {
    if(highBid && highBid > 0 && disableSoldButtons) {
      setDisableSoldButtons(false);
    }else if (highBid === 0 || highBid === null || highBid === undefined) {
      setDisableSoldButtons(true);
    }
  }, [highBid]);

  if (!saleAsset) {
    return <Subtitle>No Active Bidding</Subtitle>;
  }

  const enterBid = (newPrice, paddleServiceId) => {
    if (sale.biddingUpdatedTimestamp) {
      if (
        bidEnteredTimestamp &&
        bidEnteredTimestamp === sale.biddingUpdatedTimestamp
      ) {
        console.log('Waiting bid result');
        return;
      }

      setBidEnteredTimestamp(sale.biddingUpdatedTimestamp);
    }

    createBid({
      variables: {
        price: (newPrice || price),
        saleAssetId: saleAsset.id,
        paddleServiceId: paddleServiceId
      }
    });
  };

  const enterPreBid = (preBid, price) => {
    if (preBid.bidId) {
      if (!(confirm('Re-enter pre-bid as bid?'))) { return; }
    }

    createBidFromPreBid({ variables: { preBidId: preBid.id, price } });
  };

  const handleKeyPress = (e, action) => {
    if ('Enter' === e.nativeEvent.key) {
      (action || enterBid)();
    }
  };

  const handleKeyDownNextManual = e => {
    if ('Escape' === e.nativeEvent.key) {
      setEditNext(false);
      setNextManual('')
    }
  }

  const reservePrice = parseFloat(saleAsset.reservePrice || '0');

  const belowReserve = reservePrice && reservePrice > highBid;

  const confirmSold = () => {
    highBid && setShowConfirmSoldAlert(true);
  };

  return (
    <View onContextMenu={e => e.preventDefault()}>
      {sale.bidGroupChoiceId && (
        <>
          <Title>Buyer Making Selections</Title>
          <Split />
        </>
      )}
      {canInput ? (
        <>
          <SuggestionsContainer
            style={{backgroundColor: colors.elLightGray, padding: '5px', gap: 2, marginBottom: '0px'}}
          >
            <Label style={{width: 40}}>Step:</Label>
            {STEP_OPTIONS.map(step => (
              <Button
                key={step}
                title={numberWithDelimiter(step)}
                color={step === saleAsset.biddingStep ? colors.biddingStepGreen : colors.biddingGrayButton}
                onPress={() => updateBiddingStep(step)}
                labelStyle={{fontSize: '16px'}}
                style={{maxWidth: '60px'}}
              />
            ))}
          </SuggestionsContainer>
          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              borderBottomWidth: '3px'
            }}
          >
            <LeftContainer style={{flex: 0.25}}>
              <BorderedContainer
                style={{
                  borderLeft: 'none',
                  borderBottom: 'none',
                  backgroundColor: highBidDetails?.backgroundColor,
                  height: '100%',
                }}
              >
                <CurrentBidDetails highBidDetails={highBidDetails}/>
                <AwardBidTo
                  highBidDetails={highBidDetails}
                  awardBid={awardBid}
                />
                <View style={{ flexDirection: 'column', gap: 5 }}>
                  <Button
                    disabled={0 === bids.length}
                    title="Reverse Bid"
                    color={colors.elRed}
                    onPress={() => highBid && removeLastBid()}
                    style={{paddingTop: '15px', paddingBottom: '15px'}}
                  />
                  <Button
                    title="Undo Reverse"
                    color={colors.biddingDarkGrayButton}
                    onPress={undoRemoveLastBid}
                    disabled={!saleAsset.nextRemovedBid}
                    labelStyle={{ color: '#fff' }}
                    style={{paddingTop: '15px', paddingBottom: '15px'}}
                  />
                </View>
              </BorderedContainer>
            </LeftContainer>
            <RightContainer
              style={{flex: 0.75, paddingLeft: '5px', paddingRight: '5px', paddingTop: '5px'}}
            >
              <BidSuggestions
                saleAsset={saleAsset}
                price={price}
                setPrice={setPrice}
                enterBid={enterBid}
                highBid={highBid}
              />
            </RightContainer>
          </View>
          <View
            style={{
              display: 'inline-flex', flexDirection: 'row',
              borderBottomWidth: '3px'
            }}
          >
            <LeftContainer
              style={{
                width: '50%', padding: '10px',
                marginBottom: '-10px'
              }}
            >
              <SuggestionsContainer style={{marginBottom: '-5px'}}>
                {GOING_BUTTONS.map(({ status, color }) => (
                  <React.Fragment key={status}>
                    {status === saleAsset.biddingStatus ? (
                      <Button
                        title={`Clear - ${status}`}
                        color="gray"
                        onPress={() => updateBiddingStatus('Pending')}
                      />
                    ) : (
                      <Button
                        disabled={0 === bids?.length}
                        title={status}
                        color={color}
                        onPress={() => updateBiddingStatus(status)}
                      />
                    )}
                  </React.Fragment>
                ))}
              </SuggestionsContainer>
              <Notices
                saleId={sale.id}
                updateSale={updateSale}
              />
            </LeftContainer>
            <RightContainer
              style={{
                width: '50%', padding: '10px', borderLeftWidth: '3px'
              }}
            >
              <AssetButtons
                saleAsset={saleAsset}
                updateBiddingStatus={updateBiddingStatus}
                bidLength={bids?.length}
                reservePrice={reservePrice}
                confirmSold={confirmSold}
                bids={bids}
                sale={sale}
                setIsIf={setIsIf}
                biddingPass={biddingPass}
                disableSoldButtons={disableSoldButtons}
                setDisableSoldButtons={setDisableSoldButtons}
              />
            </RightContainer>
          </View>
        </>
      ) : (
        <>
          <View style={{flexDirection: 'row', gap: 20}}>
            <Subtitle style={{alignSelf: 'center', marginLeft: 20}}>
              Bid Step
            </Subtitle>
            <Button
              title={numberWithDelimiter(saleAsset.biddingStep)}
              color="black"
            />
          </View>
        </>
      )}

      <View style={{ flexDirection: 'row', gap: 20 }}>
        <BidHistory bids={bids} clerk={true} />
        {/* {canInput && (
          <View>
            <View
              style={{ flexDirection: 'row', justifyContent: 'space-around' }}
            >
              <Subtitle>{saleAsset.preBids.length} Pre-Bids</Subtitle>
              {saleAsset.preBids.length > 0 && (
                <Pressable onPress={() => setShowPreBidDetail(true)}>
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                </Pressable>
              )}
            </View>
            {saleAsset.preBids.length > 0 && (
              <View style={{ alignItems: 'center' }}>
                <Text>High Pre-Bid: {dollars(saleAsset.highPreBidPrice)}</Text>
                <Text>
                  {saleAsset.highPreBid.buyerUser.paddleNumber}{' - '}
                  {saleAsset.highPreBid.buyerUser.buyer.cityState}
                </Text>
                <Button
                  title="Enter as Bid Now"
                  color={saleAsset.highPreBid.bidId ? 'lightgray' : COLORS.bid}
                  onPress={() => (
                    enterPreBid(saleAsset.highPreBid, saleAsset.highPreBidPrice)
                  )}
                  labelStyle={{ color: 'black' }}
                  style={{ minHeight: 40 }}
                />
              </View>
            )}
          </View>
        )} */}
      </View>
      {showPreBidDetail && (
        <Modal
          showModal={showPreBidDetail}
          setShowModal={setShowPreBidDetail}
          modalType={saleAsset.correctedLotNumber}
        >
          <PreBidsDetail saleAsset={saleAsset} />
        </Modal>
      )}
      {showConfirmSoldAlert && (
        <Modal
          showModal={showConfirmSoldAlert}
          setShowModal={setShowConfirmSoldAlert}
          modalType="Confirm Sold?"
        >
          <SoldInfoModal
            highBid={highBid}
            highBidDetails={highBidDetails}
            saleAsset={saleAsset}
            reservePrice={reservePrice}
            belowReserve={belowReserve}
            setShowModal={setShowConfirmSoldAlert}
            bidWon={bidWon}
            isIf={isIf}
            setIsIf={setIsIf}
          />
        </Modal>
      )}
      {/* {showConfirmSoldAlert && (
        <Modal
          showModal={showConfirmSoldAlert}
          setShowModal={setShowConfirmSoldAlert}
          modalType="Confirm Sold?"
        >
          <ConfirmSoldAlert
            bid={highBidDetails}
            highBid={highBid}
            highBidDetails={highBidDetails}
            saleAsset={saleAsset}
            reservePrice={reservePrice}
            belowReserve={belowReserve}
            setShowModal={setShowConfirmSoldAlert}
            bidWon={bidWon}
          />
        </Modal>
      )} */}
      {/* {showWinnerInfoAlert && (
        <Modal
          showModal={showWinnerInfoAlert}
          setShowModal={setShowWinnerInfoAlert}
          modalType="Winner Info"
        >
          <WinnerInfoAlert
            bid={highBidDetails}
            setShowModal={setShowWinnerInfoAlert}
          />
        </Modal>
      )} */}
    </View>
  );
};

export default Bids;
